/**
 * Direct upload: the browser sends files straight to S3, the API only signs and confirms.
 * Flow: presign -> upload each file to S3 -> confirm (or cancel on failure).
 * If S3 is blocked on the user's network, the app's old server upload (`fallback`) is used instead.
 */
import {DIRECT_UPLOAD_ORG_IDS} from '../constant';
import {getCookie} from './storage';

const RETRY_DELAYS = [1000, 2000, 4000];
const REQUEST_TIMEOUT = 30000;
const S3_CONNECT_TIMEOUT = 30000; // no upload progress in this time = S3 is not reachable
const UPLOAD_FAILED = 'File upload failed. Please try again.';
const UPLOAD_UNCONFIRMED = 'We couldn\'t confirm the upload. Please check your connection before sending it again.';

const DIRECT_BLOCKED_KEY = 'ubs_direct_upload_blocked_until';
const DIRECT_RETRY_AFTER_MS = 12 * 60 * 60 * 1000;

const STRIP_METADATA_EXT = ['jpg', 'jpeg', 'jfif'];
const HEIC_EXT = ['heic', 'heif'];
const MAX_IMAGE_DIMENSION = 4096;
const JPEG_QUALITY = 0.92;
const HEIC_FAILED = 'This HEIC photo could not be converted. Please save it as JPG and try again.';

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const getExt = name => (/[.]/.test(name) ? name.split('.').pop().toLowerCase() : '');

const describeAsIs = file => ({fileName: file.name, mimeType: file.type});

const defaultHeaders = () => ({'Authorization': `Bearer ${getCookie('ubs_token')}`});

const getErrorMessage = e => e?.response?.data?.err || (e?.isUserMessage ? e.message : UPLOAD_FAILED);

export const isDirectUploadBlocked = () => {
    try {
        return Date.now() < Number(localStorage.getItem(DIRECT_BLOCKED_KEY));
    } catch (e) {
        return false;
    }
};

const markDirectUploadBlocked = () => {
    try {
        localStorage.setItem(DIRECT_BLOCKED_KEY, String(Date.now() + DIRECT_RETRY_AFTER_MS));
    } catch (e) {
        // localStorage not available
    }
};

const loadImage = blob => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
    };
    img.onerror = e => {
        URL.revokeObjectURL(url);
        reject(e);
    };
    img.src = url;
});

// Images are processed one at a time to keep memory low on mobile.
let imageQueue = Promise.resolve();

/**
 * Removes EXIF (GPS, device info) from photos before upload by redrawing them on a canvas.
 * Pass `convertHeic` (e.g. heic2any) to turn HEIC photos into JPG; without it they are sent as is.
 */
export const createImagePreparer = ({convertHeic} = {}) => {
    const isHeic = ext => !!convertHeic && HEIC_EXT.includes(ext);

    const describeFile = file => {
        const ext = getExt(file.name);
        if (isHeic(ext)) return {fileName: file.name.replace(/\.(heic|heif)$/i, '.jpg'), mimeType: 'image/jpeg'};
        if (STRIP_METADATA_EXT.includes(ext)) return {fileName: file.name, mimeType: 'image/jpeg'};
        return describeAsIs(file);
    };

    const toJpeg = async file => {
        const heic = isHeic(getExt(file.name));
        try {
            let source = file;
            if (heic) {
                source = await convertHeic(file, {toType: 'image/jpeg', quality: JPEG_QUALITY});
                if (Array.isArray(source)) source = source[0];
            }
            const img = await loadImage(source);
            const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.naturalWidth * scale);
            canvas.height = Math.round(img.naturalHeight * scale);
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
            canvas.width = 0;
            canvas.height = 0;
            if (!blob) throw new Error('canvas.toBlob returned null');
            return new File([blob], describeFile(file).fileName, {type: 'image/jpeg', lastModified: file.lastModified});
        } catch (e) {
            console.error('createImagePreparer : failed', e);
            if (heic) throw Object.assign(new Error(HEIC_FAILED), {isUserMessage: true});
            return file;
        }
    };

    const prepareFile = (file, cancelToken) => {
        const ext = getExt(file.name);
        if (!STRIP_METADATA_EXT.includes(ext) && !isHeic(ext)) return Promise.resolve(file);
        const run = imageQueue.then(() => {
            cancelToken?.throwIfRequested();
            return toJpeg(file);
        });
        imageQueue = run.catch(() => null);
        return run;
    };

    return {describeFile, prepareFile};
};

/**
 * Uploads to S3. Never rejects; resolves to:
 *   {status: 'sent', presign, result}
 *   {status: 'cancelled', presign}
 *   {status: 'failed', presign, stage, code, network, message, discarded}
 *     discarded: server removed the upload, so the app can remove its placeholder
 *     stage: 'presign' | 'upload' | 'confirm',  network: the request got no answer
 */
const uploadToS3 = async ({http, api, getHeaders = defaultHeaders, orgId, module, context = {}, files, describeFile = describeAsIs, prepareFile, cancelToken, onProgress}) => {
    const config = (token, extra = {}) => ({cancelToken: token, headers: getHeaders(), ...extra});

    // Retries on network errors and 5xx only.
    const postWithRetry = async (path, body, token) => {
        for (let attempt = 0; ; attempt++) {
            try {
                return await api.post(path, body, config(token, {timeout: REQUEST_TIMEOUT}));
            } catch (e) {
                const retryable = !http.isCancel(e) && (!e.response || e.response.status >= 500);
                if (!retryable || attempt >= RETRY_DELAYS.length) throw e;
                await wait(RETRY_DELAYS[attempt]);
            }
        }
    };

    // Own cancel token so a timeout is not mistaken for the user pressing Cancel.
    const postToS3 = (url, form, onUploadProgress) => {
        const s3Source = http.CancelToken.source();
        if (cancelToken) cancelToken.promise.then(cancel => s3Source.cancel(cancel?.message));
        let timedOut = false;
        const timer = setTimeout(() => {
            timedOut = true;
            s3Source.cancel();
        }, S3_CONNECT_TIMEOUT);
        return http.post(url, form, {
            cancelToken: s3Source.token,
            onUploadProgress: e => {
                clearTimeout(timer);
                onUploadProgress(e);
            },
        }).catch(e => {
            if (timedOut) throw Object.assign(new Error('S3 did not respond'), {isNetwork: true});
            throw e;
        }).finally(() => clearTimeout(timer));
    };

    let presign = null, lastPercent = 0, stage = 'presign';
    const reportProgress = percent => {
        percent = Math.min(99, Math.floor(percent));
        if (percent > lastPercent) {
            lastPercent = percent;
            if (onProgress) onProgress(percent);
        }
    };

    try {
        presign = (await api.post('/file/direct/presign', {
            orgId,
            module,
            context,
            files: files.map(({file, meta}) => ({size: file.size, ...describeFile(file), lastModified: new Date().getTime(), ...meta})),
        }, config(cancelToken))).data;
        stage = 'upload';

        for (let i = 0; i < files.length; i++) {
            const uploadFile = prepareFile ? await prepareFile(files[i].file, cancelToken) : files[i].file;
            const {url, fields} = presign.files[i];
            const form = new FormData();
            Object.keys(fields).forEach(key => form.append(key, fields[key])); // S3 needs these before the file
            form.append('file', uploadFile);
            await postToS3(url, form, e => reportProgress((i + (e.total ? e.loaded / e.total : 0)) / files.length * 100));
        }
        stage = 'confirm';

        const confirmed = await postWithRetry('/file/direct/confirm', {orgId, uploadId: presign.uploadId}, cancelToken);
        return {status: 'sent', presign, result: confirmed.data};
    } catch (e) {
        if (http.isCancel(e)) {
            if (presign) api.post('/file/direct/cancel', {orgId, uploadId: presign.uploadId}, config()).catch(() => null);
            return {status: 'cancelled', presign};
        }

        const code = e?.response?.status || null;
        const network = !!e?.isNetwork || (!!e?.request && !e?.response);
        const message = getErrorMessage(e);
        if (!presign) return {status: 'failed', presign, stage, code, network, message, discarded: false};

        // Cancel tells us whether the upload was already confirmed (e.g. confirm response got lost).
        const outcome = await postWithRetry('/file/direct/cancel', {orgId, uploadId: presign.uploadId}).then(res => res.data).catch(() => null);
        if (outcome?.confirmed) return {status: 'sent', presign, result: null};
        if (!outcome) return {status: 'failed', presign, stage, code, network, message: UPLOAD_UNCONFIRMED, discarded: false};
        return {status: 'failed', presign, stage, code, network, message, discarded: true};
    }
};

const runFallback = async (http, fallback) => {
    try {
        return {status: 'sent', via: 'fallback', presign: null, result: await fallback()};
    } catch (e) {
        if (http.isCancel(e)) return {status: 'cancelled', via: 'fallback', presign: null};
        return {status: 'failed', via: 'fallback', presign: null, code: e?.response?.status || null, message: getErrorMessage(e), discarded: false};
    }
};

/**
 * Never rejects. Returns the result with `via: 'direct' | 'fallback'`.
 *
 * @param http          app's axios (apps use a different axios version than the framework)
 * @param api           app's axios instance for the backend API
 * @param getHeaders    optional, default sends the ubs_token
 * @param module        backend module name, e.g. 'chat'
 * @param context       module data for presign, e.g. chat: room, randomId
 * @param files         [{file, meta}]
 * @param describeFile  optional, file name and type after prepareFile
 * @param prepareFile   optional, changes the file before upload (e.g. createImagePreparer)
 * @param cancelToken   optional, from the app's axios
 * @param onProgress    optional, called with 1..99
 * @param fallback      optional () => Promise, the app's old server upload, used when S3 is blocked
 */
export const uploadDirect = async options => {
    const {http, orgId, fallback} = options;
    const directAllowed = DIRECT_UPLOAD_ORG_IDS.includes(String(orgId || ''));
    if (fallback && (!directAllowed || isDirectUploadBlocked())) return runFallback(http, fallback);

    const upload = await uploadToS3(options);
    const s3Blocked = upload.status === 'failed' && upload.stage === 'upload' && upload.network && upload.discarded;
    if (fallback && s3Blocked) {
        markDirectUploadBlocked();
        return runFallback(http, fallback);
    }
    return {...upload, via: 'direct'};
};
